import React from 'react';
import { useQueryClient } from 'react-query';
import __ from "lodash";
import { validate as isValidUuid } from 'uuid';
import { makeStyles, withStyles, useTheme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Popper from "@material-ui/core/Popper";
import { useCompanyContacts } from "../../../services/companies";

/* eslint-disable no-use-before-define */
const styles = (theme) => ({
  popper: {
    maxWidth: "fit-content"
  },
});

const formatInputValue = (value, records, alwaysShowCompanyName) => {
  let inputValue= value||"";
  if (records.data) {
    const companyData = __.find(records.data, {companyUuid: value});
    if (companyData) {
      let companyName = companyData.companyName;
      if (companyData.branchCode) {
        companyName = `${companyData.companyName} (${companyData.branchCode})`;
      }
      inputValue = companyData.companyCode||companyName;
      if (alwaysShowCompanyName) {
        inputValue = companyName;
      }
    }
  }
  return inputValue;
}

const getContact = (contactMethods) => {
  const foundMethods = {};
  if (contactMethods && Array.isArray(contactMethods)) {
    for (let method = 0; method < contactMethods.length; method++) {
      const contactMethod = contactMethods[method];
      if (__.has(contactMethod, "phone")) {
        if ( !__.has(foundMethods, "phone")) {
          foundMethods["phone"] = contactMethod["phone"];
          continue;
        }
        if (!__.has(foundMethods, "altPhone")) {
          foundMethods["altPhone"] = contactMethod["phone"];
          continue;
        }
      }
      if (__.has(contactMethod, "email")) {
        if ( !__.has(foundMethods, "email")) {
          foundMethods["email"] = contactMethod["email"];
          continue;
        }
      }
    }
  }
  return foundMethods;
}

function buildReturnObject(props, record, defaultDisplayFormatter, value) {
  const returnObject = props.allowEmpty ? {
    value, found: true, data: {
      companyContactUuid: null,
      title: "",
      forename: "",
      surname: "",
      phone: "",
      email: "",
      displayText: ""
    }
  } : { value, found: false, data: {} };
  if (record) {
    returnObject.found = true;
    const methods = getContact(record.contactMethods);
    returnObject.data = {
      companyContactUuid: record.companyContactUuid,
      title: record.title,
      forename: record.forename,
      surname: record.surname,
      phone: methods?.phone || "",
      email: methods?.email || "",
      displayText: props.displayFormatter && typeof props.displayFormatter === "function" ? props.displayFormatter(record, methods) : defaultDisplayFormatter(record, methods)
    };
    returnObject.value = record.companyContactUuid;
  }
  return returnObject;
}

const CompanyContactLookup = props => {

  const theme = useTheme();
  const classes = makeStyles(theme => ({
    readOnly: {
      color: theme.palette.text.primary
    }
  }))(theme);

  const queryClient = useQueryClient();
  const extraKeys = props.contactType?[props.companyUuid, props.contactType]:[props.companyUuid];
  const records = useCompanyContacts({queryClient, useCache: true, extraKeys});

  const defaultDisplayFormatter = (record, methods) => {
    return `${record.title ? record.title + " " : ""} ${record.forename ?? ""} ${record.surname ?? ""} ${methods.email?`(${methods.email})`:``}`;
  }


  const handleChange = (event, record) => {
    if (event.key === 'Enter') {
      event.stopPropagation()
    }
    if (props.returnType === "object") {
      if (props.freeSolo && !isValidUuid(event.target.value)) {
        props.onChange({ value: event.target.value, found: false, data: {} });
        return;
      }
      const returnObject = buildReturnObject(props, record, defaultDisplayFormatter, record?.companyContactUuid||"");
      props.onChange(returnObject);
    } else {
      if (!record) {
        props.onChange("");
      } else {
        props.onChange(record.companyContactUuid);
      }
    }
  }
  const handleInputChange = (event, value) => {
    if (!event || (event.type !== 'change' && !(((event.type === 'blur' && props.allowFreeInput !== true) || event.type==='click') && (value || props.allowEmpty)))) return;
    if (props.returnType === "object") {
      const record = __.find(records.data, { companyContactUuid: value });
      const returnObject = buildReturnObject(props, record, defaultDisplayFormatter, value);
      props.onChange(returnObject);
    } else {
      props.onChange(value);
    }
  }

  const DropdownList = function (props) {
    return <Popper {...props} style={styles.popper} placement="bottom-start" />;
  };

  const defaultFilterOptions = (options, state) => {
    if (state.inputValue) {
      const searchText = state.inputValue.toUpperCase();
      const filteredOptions = __.filter(options, (contact) => {
        let name = `${contact.forename ?? ""} ${contact.surname ?? ""}`;
        return (contact.companyContactUuid === state.inputValue || name.toUpperCase().indexOf(searchText) >= 0)
      });
      return filteredOptions;
    } else return options.slice(0, 300);
}
  const filterOptions = props.filterOptions || defaultFilterOptions;

  const renderOption = (record) => {
    const returnObject = buildReturnObject(props, record, defaultDisplayFormatter, record?.companyContactUuid||"");
    return <>{returnObject?.data?.displayText}</>
  }

  if (props.type === "render") {
    const record = __.find(records.data, { companyContactUuid: props.value });
    return (<div>{renderOption(record)}</div>);
  } else {
    return (
      <Autocomplete
        id={props.id}
        freeSolo={props.freeSolo}
        disableClearable={true}
        autoSelect={(records && records.data && !props.allowEmpty)?true:false}
        autoHighlight={true}
        inputValue={formatInputValue(props.value, records)}
        options={records.data||[]}
        filterOptions={filterOptions}
        PopperComponent={DropdownList}
        getOptionSelected={(option, value) => option.companyContactUuid === value.companyContactUuid}
        getOptionLabel={(option) => option.companyContactUuid}
        onChange={handleChange}
        onInputChange={handleInputChange}
        disabled={props.disabled||props.readOnly}
        renderOption={(option) => (
          <React.Fragment key={`companyContact-lookup-${option.companyContactUuid}`}>
            {renderOption(option)}
          </React.Fragment>
        )}
        style={{
          width: (props.width||"100%"),
        }}
        renderInput={(params) => {
          let value = params.inputProps.value||"";
          const record = __.find(records.data, {companyContactUuid: value});
          if (record) {
            const returnObject = buildReturnObject(props, record, defaultDisplayFormatter, value||"");
            value = returnObject?.data?.displayText||"";
          }
          const extraProps = {};
          if (props.readOnly) {
            extraProps.className = classes.readOnly;
          }
          const inputProps = {
            ...props.inputProps,
            ...params.inputProps,
            ...extraProps,
            value
          }
          return (<TextField
            {...params}
            label={props.label}
            placeholder={props.placeHolder}
            inputProps={{...inputProps}}
            InputLabelProps={{shrink: props.shrink}}
          />)
        }}
      />
    )
  }
}

export default withStyles(styles) (CompanyContactLookup);
export {
  CompanyContactLookup as companyContactLookup
}


